import type { ComponentProps, SubmitEvent } from "react"
import { useEffect, useMemo, useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Label from "@/v2-ui/label"
import Checkbox from "@/v2-ui/checkbox"
import Input from "@/v2-ui/input"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/v2-ui/inputGroup"
import {
  getPasswordValidity,
  generatePassword,
} from "@/v2-backoffice-client/user/resetPassword/user.resetPassword.utils"

export type UserRecreateIdentityFormValues = {
  email: string
  password: string
  sendAccountEmail: boolean
}

type BaseProps = Omit<ComponentProps<"form">, "onSubmit">
export type UserRecreateIdentityFormProps = BaseProps & {
  defaultEmail?: string
  onSubmit(
    values: UserRecreateIdentityFormValues,
    e: SubmitEvent<HTMLFormElement>,
  ): void
  onValidityChange?(isValid: boolean): void
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function UserRecreateIdentityForm(props: UserRecreateIdentityFormProps) {
  const { defaultEmail, onSubmit, onValidityChange, ...formProps } = props
  const [email, setEmail] = useState(defaultEmail ?? "")
  const [password, setPassword] = useState("")
  const [sendAccountEmail, setSendAccountEmail] = useState(false)

  const { results, isValid: isPasswordValid } = useMemo(() => {
    return getPasswordValidity(password)
  }, [password])

  const isEmailValid = EMAIL_PATTERN.test(email)
  const isValid = isEmailValid && isPasswordValid

  useEffect(() => {
    onValidityChange?.(isValid)
  }, [isValid, onValidityChange])

  const showRules = password.length > 0

  return (
    <form
      id="recreate-identity-form"
      className="space-y-4 py-4"
      onSubmit={(e) => {
        onSubmit({ email, password, sendAccountEmail }, e)
      }}
      {...formProps}
    >
      <div className="space-y-2">
        <Label htmlFor="recreateEmail">New login email</Label>
        <Input
          id="recreateEmail"
          type="email"
          required
          autoFocus
          placeholder="user@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
          }}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="recreatePassword">Temporary password</Label>
        <InputGroup size="md">
          <InputGroupInput
            id="recreatePassword"
            type="text"
            required
            placeholder="Enter temporary password..."
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
            }}
          />
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              size="md"
              variant="ghost"
              onClick={() => {
                setPassword(generatePassword())
              }}
            >
              <Icon src="shuffle" size={14} />
              Generate
            </InputGroupButton>
          </InputGroupAddon>
        </InputGroup>
      </div>

      {showRules && (
        <ul className="space-y-1 text-sm">
          {results.map((rule) => (
            <li
              key={rule.label}
              className={cn(
                "flex items-center gap-2",
                rule.passed ? "text-emerald-600" : "text-muted-foreground",
              )}
            >
              <Icon
                className={!rule.passed ? "opacity-40" : undefined}
                src={rule.passed ? "check" : "x"}
              />
              {rule.label}
            </li>
          ))}
        </ul>
      )}

      <Label className="flex items-center gap-2 text-sm font-normal">
        <Checkbox
          checked={sendAccountEmail}
          onCheckedChange={(checked) => {
            setSendAccountEmail(checked === true)
          }}
        />
        Email the new credentials to the user
      </Label>
    </form>
  )
}

export default UserRecreateIdentityForm
